import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/lib/useSeo";
import {
  AuthShell,
  PrimaryButton,
  TextField,
  ErrorBox,
  OtpBoxes,
  EyeToggle,
} from "@/components/AuthKit";

/**
 * VerifyEmail — 1:1 port of iOS `src/app/(auth)/verify.tsx`.
 * OTP entry (6 boxes) + optional password re-entry (when arriving via an
 * unverified login), 60-second resend countdown.
 */
export default function VerifyEmail() {
  const navigate = useNavigate();
  const {
    confirmAccountVerification,
    resendAccountVerification,
    getPendingSignupEmail,
    apiError,
    triggerWelcome,
  } = useAuth();

  const pendingEmail = getPendingSignupEmail ? getPendingSignupEmail() : "";
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [needsPassword, setNeedsPassword] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [countdown, setCountdown] = useState(60);
  const otpResetKey = useRef(0);

  useEffect(() => {
    if (!pendingEmail) navigate("/signup", { replace: true });
  }, [pendingEmail, navigate]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  const submit = async (otpOverride) => {
    if (loading) return;
    setErr("");
    const otp = (otpOverride ?? code).replace(/\D/g, "");
    if (otp.length !== 6) return setErr("Enter the 6-digit code from your email.");
    if (needsPassword && !password) return setErr("Please enter your password.");

    setLoading(true);
    try {
      await confirmAccountVerification(otp, needsPassword ? password : undefined);
      triggerWelcome && triggerWelcome("Welcome to BrontoY");
      navigate("/", { replace: true });
    } catch (e2) {
      if (e2?.needsPassword) {
        setNeedsPassword(true);
        setErr("Please enter your password to complete verification.");
      } else {
        setErr(apiError(e2));
      }
      setCode("");
      otpResetKey.current += 1;
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await resendAccountVerification();
      toast.success("A new code has been sent.");
      setCountdown(60);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AuthShell
      testId="verify-screen"
      title="Verify your email"
      subtitle={
        pendingEmail
          ? `We sent a 6-digit code to ${pendingEmail}. Enter it below to finish creating your account.`
          : "Enter the 6-digit code sent to your email."
      }
    >
      <Seo
        title="Verify your email — BrontoY"
        description="Enter the 6-digit verification code sent to your email to finish creating your BrontoY account."
        canonicalPath="/verify"
        noindex
      />
      <div className="flex flex-col mt-8">
        {needsPassword ? (
          <div className="flex flex-col gap-1.5 mb-6" data-testid="verify-password-block">
            <label
              className="text-muted-foreground font-semibold"
              style={{ fontSize: 13, letterSpacing: "0.02em" }}
              htmlFor="verify-password"
            >
              CONFIRM YOUR PASSWORD
            </label>
            <TextField
              id="verify-password"
              type={showPw ? "text" : "password"}
              autoCapitalize="none"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              rightAdornment={
                <EyeToggle show={showPw} onToggle={() => setShowPw((s) => !s)} testId="verify-toggle-pw" />
              }
              testId="verify-password-input"
            />
            <p className="text-muted-foreground text-xs mt-1">
              You&apos;re finishing verification for an existing account. Re-enter the password you used at signup.
            </p>
          </div>
        ) : null}

        {/* OTP boxes */}
        <OtpBoxes
          key={otpResetKey.current}
          length={6}
          value={code}
          onChange={setCode}
          onComplete={(v) => submit(v)}
          testId="verify-otp"
        />

        {err ? (
          <div className="mt-5">
            <ErrorBox testId="verify-error">{err}</ErrorBox>
          </div>
        ) : null}

        <PrimaryButton
          label="Verify & create account"
          onClick={() => submit()}
          loading={loading}
          className="mt-6"
          testId="verify-submit-btn"
        />

        {/* Resend row */}
        <div className="flex items-center justify-center mt-5">
          {countdown > 0 ? (
            <span className="text-muted-foreground text-sm" data-testid="verify-countdown">
              Resend available in {countdown}s
            </span>
          ) : (
            <button
              type="button"
              onClick={resend}
              className="text-foreground font-bold text-sm underline underline-offset-4 hover:opacity-80"
              data-testid="verify-resend-btn"
            >
              Resend code
            </button>
          )}
        </div>

        {/* Wrong email → back to signup */}
        <div className="flex items-center justify-center mt-7 text-sm">
          <span className="text-muted-foreground">Wrong email?&nbsp;</span>
          <button
            type="button"
            onClick={() => navigate("/signup", { replace: true })}
            className="text-foreground font-bold hover:opacity-80"
            data-testid="verify-back-to-signup"
          >
            Start over
          </button>
        </div>
      </div>
    </AuthShell>
  );
}
